import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EventSource } from 'eventsource';
import { AuthService } from 'src/app/auth/service/auth.service';
import { ApiUsage, CreditBalanceBySourceInner, UsageService } from 'ldt-billing-service-api';
import { environment } from 'src/environments/environment';
import * as dayjs from 'dayjs';
import * as Sentry from '@sentry/angular';
import { freeTierBase, PEOPLE_RECORD_FREE_QUOTA } from 'src/app/shared/utils/free-tier';

const PEOPLE_QUOTA_WARNING_THRESHOLD = 90;

// The object that observers will receive
export interface CreditUsage {
  orgId: string;
  peopleQuota: number | null;
  peopleUsage?: number;
  peopleUsagePercentage?: number;
  peopleQuotaWarning?: 'warn' | 'error' | null;
  resetDate: Date;
  // The org's recurring monthly people-credit allowance, from org settings:
  // the plan's per-cycle base (a metered plan's record limit, or the legacy
  // monthly free tier) plus any recurring monthly credit grant from the
  // credits settings block. The balance endpoint never reports the monthly
  // allowance among its credit rows, so displays must read it from here.
  // 0 when nothing renews.
  monthlyCredit: number;
}

// In-memory storage of usage by usage_type, as provided by the API. The initial getOrgApiUsage call will
// provide values for all usage_types, but the SSE stream will only provide values for one usage_type at a time.
// This holds the elements that make up our running total of usage.
interface CreditUsageDetails {
  usage_key: string;
  total_usage: number;
}

@Injectable({
  providedIn: 'root',
})
export class CreditUsageService {
  private orgApiUsageStream?: Subscription;
  private usageInitSub?: Subscription;

  private creditUsage: CreditUsage | null = null;
  creditUsage$ = new BehaviorSubject<CreditUsage | null>(null);

  private creditUsageDetails: CreditUsageDetails[] = [];

  private currentOrgId: string | null = null;

  constructor(
    private authService: AuthService,
    private usageService: UsageService
  ) {
    this.authService.$getSelectedOrg.subscribe((org) => {
      this.currentOrgId = org?.id ?? null;

      // Tear down the previous org's usage plumbing immediately so a late
      // initial-usage response or SSE event can't bleed into the newly
      // selected org (the old stream would otherwise stay open forever when
      // the new org is unlimited and never starts a replacement).
      this.usageInitSub?.unsubscribe();
      this.orgApiUsageStream?.unsubscribe();
      this.creditUsageDetails = [];

      if (!org) {
        this.creditUsage$.next(null);
        return;
      }

      // Base allowance: the org's explicit record limit whatever the subType —
      // a recordLimit stored under 'free' (or no subType) is still an enforced
      // monthly base (see the admin org-settings editor), and an explicit 0 is
      // a real base under credit-mode semantics (nullish check). Unset falls
      // back to the legacy monthly free tier (0 unless freeTier: 'monthly').
      // The pushed usage-credit balance stacks on top of the base, mirroring
      // how identity computes the enforced quota (base + credit). The base
      // renews every cycle, so it is also the recurring part of the quota
      // that displays show as "monthly credits".
      const people = org.settings?.people;
      const credit = people?.recordLimitCredit || 0;
      let monthlyBase = people?.recordLimit ?? freeTierBase(people, PEOPLE_RECORD_FREE_QUOTA);
      let quota: number | null = monthlyBase + credit;

      if (people?.subType === 'unlimited') {
        quota = null;
        monthlyBase = 0;
      } else if (people?.subType === 'metered' && people.recordLimit == null) {
        // This shouldn't happen (the free-tier fallback applies); raise to us
        Sentry.captureException('Org has a metered plan with no record limit. Org ID: ' + org.id);
      }

      // For now (upcoming API changes will change this) we use the subscriptionType to determine the quota, or null if no quota
      this.creditUsage = {
        orgId: org.id,
        peopleQuota: quota,
        peopleQuotaWarning: null,
        resetDate: this.getNextResetDate(org.settings?.people?.subscriptionAnchorDate),
        // The recurring per-cycle base plus any recurring monthly credit grant
        // from the credits settings block (not in the typed Org settings yet;
        // backend supports it, undocumented).
        monthlyCredit:
          monthlyBase + (Number((org.settings as any)?.credits?.recordLimitMonthlyCredit) || 0),
      };
      this.creditUsage$.next(this.creditUsage);

      // Consumers of this service should check if this is null to know if the org has a quota
      if (this.creditUsage.peopleQuota === null) {
        return;
      }

      // Get the initial usage details from the billing API
      this.usageInitSub = this.usageService.getOrgApiUsage(org.id, 'principal', false).subscribe({
        next: (usage) => {
          // Ignore a response that arrives after the selected org changed
          if (org.id !== this.currentOrgId) {
            return;
          }

          // Map usage to our interface
          this.creditUsageDetails = usage.usage
            .filter((entry) => entry.product_name === 'people')
            .map((entry) => ({
              usage_key: this.getApiUsageKey(entry),
              total_usage: entry.total_usage,
            }));

          // Calculate the total usage and stats
          this.calculateTotalUsage();

          // Update observers
          this.creditUsage$.next(this.creditUsage);

          // Start the SSE stream
          this.getOrgApiUsageStream(org.id);
        },
        error: (error) => {
          console.error('error', error);
        },
      });
    });
  }

  // Live remaining people-record credit for the selected org, split by source
  // (one-time, purchased, ...). Computed server-side by applying the current
  // cycle's usage against the credit rows, so it is fetched on demand (e.g.
  // when the breakdown popover opens) rather than cached. The monthly
  // allowance is never among these rows: it comes from org settings (see
  // CreditUsage.monthlyCredit). Emits [] when the org has no remaining
  // credit, and null when the lookup fails.
  getPeopleCreditBreakdown(): Observable<CreditBalanceBySourceInner[] | null> {
    const orgId = this.currentOrgId;
    if (!orgId) {
      return of(null);
    }
    return this.usageService.getOrgCreditBalance(orgId).pipe(
      map((balance) => balance?.['people']?.['recordLimit']?.by_source ?? []),
      catchError(() => of(null))
    );
  }

  // Calculate the total usage and stats to send to consumers so they have very little work to do
  private calculateTotalUsage() {
    this.creditUsage!.peopleUsage = this.creditUsageDetails.reduce(
      (acc, curr) => acc + curr.total_usage,
      0
    );
    // A credit-mode org with no credit balance has a real quota of 0: there is
    // no capacity at all, so the meter reads full (100%) even at 0 usage — an
    // empty bar would suggest headroom (this also avoids dividing by zero).
    this.creditUsage!.peopleUsagePercentage =
      this.creditUsage!.peopleQuota! > 0
        ? (this.creditUsage!.peopleUsage! / this.creditUsage!.peopleQuota!) * 100
        : 100;
    this.creditUsage!.peopleQuotaWarning =
      this.creditUsage!.peopleUsagePercentage && this.creditUsage!.peopleUsagePercentage >= 100
        ? 'error'
        : this.creditUsage!.peopleUsagePercentage &&
            this.creditUsage!.peopleUsagePercentage >= PEOPLE_QUOTA_WARNING_THRESHOLD
          ? 'warn'
          : null;
  }

  // Starts streaming from the SSE endpoint to get updates on usage. The endpoint provides the current total for
  // the specified usage_type. Every time a new value is received, it replaces the existing value in the in-memory
  // storage of usage. Then we recalculate the total usage and stats to send to consumers.
  private getOrgApiUsageStream(orgId: string) {
    // If there is already a stream, unsubscribe from it
    if (this.orgApiUsageStream) {
      this.orgApiUsageStream.unsubscribe();
    }

    // Create a new stream
    this.orgApiUsageStream = new Observable<any>((observer) => {
      const url = environment.billingApiBasePath + '/' + orgId + '/usage/stream';

      // We use the eventsource package instead of angular native so we can set auth headers
      const eventSource = new EventSource(url, {
        fetch: (input: any, init: any) =>
          fetch(input, {
            ...init,
            headers: {
              ...init!.headers,
              Authorization: `Bearer ${this.authService.getAccessTokenValue}`,
            },
          }),
      });

      // When a message is received, push it into the observable stream
      eventSource.onmessage = (event: any) => {
        // Parse the message as JSON and push it into the observable stream, raising an error if it's not valid
        try {
          observer.next(JSON.parse(event.data));
        } catch (error) {
          observer.error(error);
        }
      };

      // If there is an error with the SSE stream, notify the observer
      eventSource.onerror = (error: any) => {
        observer.error(error);
        eventSource.close();
      };

      // On unsubscribe, close the EventSource connection
      return () => {
        eventSource.close();
      };
    }).subscribe({
      next: (data: ApiUsage) => {
        // Belt and braces: the stream is torn down on org switch, but never
        // apply an event that isn't for the currently selected org.
        if (orgId !== this.currentOrgId || data.product_name !== 'people') {
          return;
        }

        // find the matching credit usage details by type and update the total usage or add the new type
        const matchingUsage = this.creditUsageDetails.find(
          (detail) => detail.usage_key === this.getApiUsageKey(data)
        );
        if (matchingUsage) {
          matchingUsage.total_usage = data.total_usage;
        } else {
          // If the usage_type is not in the list, add it
          this.creditUsageDetails.push({
            usage_key: this.getApiUsageKey(data),
            total_usage: data.total_usage,
          });
        }
        this.calculateTotalUsage();
        this.creditUsage$.next(this.creditUsage);
      },
      error: (error) => {
        console.error('error', error);
      },
    });
  }

  private getApiUsageKey(usage: ApiUsage): string {
    return `${usage.org_id}-${usage.principal_id}-${usage.product_name}-${usage.usage_type}`;
  }

  // Returns the next occurrence of the anchor day-of-month (e.g. anchor=6 → next 6th)
  private getNextResetDate(anchorDate?: string | Date): Date {
    const fallback = dayjs().add(1, 'month').startOf('month').toDate();
    if (!anchorDate) {
      return fallback;
    }

    const anchor = dayjs(anchorDate);
    if (!anchor.isValid()) {
      return fallback;
    }

    const anchorDay = anchor.date(); // day of month subscription was created
    const today = dayjs();
    const targetMonth = today.date() >= anchorDay ? today.add(1, 'month') : today;
    const dayInTargetMonth = Math.min(anchorDay, targetMonth.daysInMonth());

    return targetMonth.date(dayInTargetMonth).startOf('day').toDate();
  }
}
